
export enum AppState {
  INITIALIZING = 'INITIALIZING',
  UPLOAD = 'UPLOAD',
  EDITOR = 'EDITOR',
  PROCESSING = 'PROCESSING',
  RESULT = 'RESULT',
  ERROR = 'ERROR'
}

export interface ImageState {
  originalFile: File | null;
  originalUrl: string | null;
  processedUrl: string | null;
  maskDataUrl: string | null; // For debugging or history if needed
}

export interface DeclutterOptions {
  prompt: string;
  brushSize: number;
}

export interface WatermarkSettings {
  imageUrl: string;
  x: number; // Normalized 0-1
  y: number; // Normalized 0-1
  scale: number; // Normalized scale relative to image width
  opacity: number; // 0-1
}

export interface UserProfile {
  id: string;
  email: string;
  credits: number;
  subscription_plan?: string;
  stripe_customer_id?: string;
  referral_code?: string;
  language?: string;
}

export interface ReferralReward {
  id: string;
  bonus_credits: number;
  referrer_bonus_credits?: number;
  purchased_credits: number;
  created_at: string;
  referred_email: string;
  referred_name?: string;
  reward_kind?: string;
  status?: string;
}

export interface ReferralStats {
  success: boolean;
  referral_code: string;
  signup_count: number;
  friends_joined?: number;
  total_bonus_credits: number;
  credits_earned?: number;
  invite_base_url?: string;
  rewards: ReferralReward[];
}

export interface GeneratedImage {
  id: string;
  user_id: string;
  input_url: string;
  output_url: string;
  prompt?: string;
  status: string;
  created_at: string;
  type?: string; // Added to match new database schema
  service_type?: 'photoclear' | 'staging'; // Identifies which app created the image
  ai_classification?: 'AI_ASSISTED' | 'AI_MODIFIED' | 'AI_GENERATED' | null;
  ai_operation?: string | null;
  ai_created_at?: string | null;
  source_asset_id?: string | null;
  visible_disclosure_applied?: boolean | null;
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  amount: number;
  type: string;
  description: string;
  created_at: string;
}

export interface Price {
  id: string;
  product_id: string;
  active: boolean;
  currency: string;
  type: 'one_time' | 'recurring';
  unit_amount: number;
  interval?: string;
  interval_count?: number;
}

export interface Product {
  id: string;
  active: boolean;
  name: string;
  description?: string;
  image?: string;
  metadata?: {
    credits?: string; // lowercase
    bonus?: string;
    popular?: string;
    [key: string]: any;
  };
  prices?: Price[];
}

export interface AdminUser {
  user_id: string;
  role: 'admin' | 'superadmin';
  created_at: string;
}

export interface PromoCode {
  id: string;
  code: string;
  credits: number;
  max_uses: number | null;
  current_uses: number;
  expires_at: string | null;
  active: boolean;
  created_by: string | null;
  description: string | null;
  created_at: string;
}

export interface PromoRedemption {
  id: string;
  promo_code_id: string | null;
  code: string;
  user_id: string;
  credits_granted: number;
  redeemed_at: string;
}

export interface AdminAction {
  id: string;
  admin_id: string | null;
  action_type: 'grant_credits' | 'create_promo' | 'update_promo' | 'deactivate_promo';
  target_user_id: string | null;
  details: any;
  created_at: string;
}

export interface RedeemPromoResult {
  success: boolean;
  error?: 'NOT_AUTHENTICATED' | 'INVALID_CODE' | 'CODE_INACTIVE' | 'CODE_EXPIRED' | 'CODE_EXHAUSTED' | 'ALREADY_REDEEMED';
  credits_added?: number;
  code?: string;
}

// =========================================
// BEHAVIOR MONITORING TYPES
// =========================================

export type EventType = 
  | 'magic_link_requested'
  | 'login'
  | 'generation_started'
  | 'generation_completed'
  | 'generation_failed'
  | 'credits_granted'
  | 'credits_used'
  | 'payment_completed'
  | 'promo_redeemed'
  | 'user_status_changed'
  | 'ip_control_changed'
  | 'domain_control_changed';

export type UserRiskStatus = 'ok' | 'watch' | 'restricted' | 'banned';

export type ControlStatus = 'ok' | 'throttled' | 'blocked';

export type DomainControlStatus = 'ok' | 'throttled' | 'require_payment' | 'blocked';

export interface EventLog {
  id: string;
  created_at: string;
  event_type: EventType;
  user_id: string | null;
  user_email: string | null;
  ip_address: string | null;
  user_agent: string | null;
  metadata: Record<string, any>;
}

export interface UserWithStatus {
  id: string;
  email: string;
  credits: number;
  created_at: string;
  risk_status: UserRiskStatus;
  status_reason: string | null;
  status_updated_at: string | null;
  marketing_consent?: boolean;
  marketing_consent_at?: string | null;
  event_count: number;
  last_activity: string | null;
}

export interface UserTimeline {
  user: {
    id: string;
    email: string;
    credits: number;
    created_at: string;
    risk_status: UserRiskStatus;
    status_reason: string | null;
    status_updated_at: string | null;
    signup_app: string | null;
  };
  events: EventLog[];
}

/** Admin: single payment/purchase/bonus row from credit_transactions or billing_transactions */
export interface UserPaymentRow {
  id: string;
  amount: number;
  type: string;
  description: string;
  created_at: string;
  stripe_session_id: string | null;
  amount_cents?: number | null;
  currency?: string | null;
  status?: string | null;
}

/** Admin: what a user has paid and consumed, for the user detail page */
export interface UserCostSummary {
  plan: string | null;
  plan_id: string | null;
  currency: string | null;
  stripe_customer_id: string | null;
  balance: number | null;
  total_paid_cents: number;
  credits_granted: number;
  credits_used: number;
  credits_refunded: number;
}

export interface IPAnalytics {
  ip_address: string;
  event_count: number;
  user_count: number;
  email_count: number;
  emails?: string[];
  last_activity: string;
  first_activity: string;
  control_status: ControlStatus;
  throttle_multiplier: number;
  control_reason: string | null;
}

export interface DomainAnalytics {
  domain: string;
  user_count: number;
  event_count: number;
  last_activity: string;
  control_status: DomainControlStatus;
  throttle_multiplier: number;
  control_reason: string | null;
}

export interface ActivityFeedFilters {
  eventType?: EventType;
  userId?: string;
  ipAddress?: string;
  limit?: number;
  offset?: number;
}

export interface RateLimitCheck {
  allowed: boolean;
  reason?: 'domain_blocked' | 'ip_blocked' | 'rate_limited';
  message?: string;
  require_payment?: boolean;
}

// =========================================
// GENERATIONS & CREDIT LEDGER TYPES
// =========================================

export type GenerationStatus = 'queued' | 'started' | 'completed' | 'failed';

export interface Generation {
  id: string;
  created_at: string;
  user_id: string | null;
  user_email: string | null;
  status: GenerationStatus;
  credits_used: number;
  input_asset_path: string | null;
  output_image_path: string | null;
  ip_address: string | null;
  metadata: Record<string, any>;
  completed_at: string | null;
  error_message: string | null;
}

/** Cross-app resource row from admin_get_user_resources */
export type UserResourceKind =
  | 'generation'
  | 'imagemotion_project'
  | 'tour'
  | 'brochure'
  | 'showcase'
  | 'pro_order'
  | 'videomotion_project'
  | 'recording';

export interface UserResource {
  id: string;
  kind: UserResourceKind;
  app: string;
  title: string;
  status: string;
  created_at: string;
  credits_used: number | null;
  preview_path: string | null;
  output_path: string | null;
  link_url: string | null;
  meta: Record<string, any> | null;
}

export type CreditRefType = 'generation' | 'purchase' | 'promo' | 'admin_grant' | 'refund' | 'welcome_bonus';

export interface CreditLedgerEntry {
  id: string;
  created_at: string;
  user_id: string;
  delta: number;
  reason: string;
  ref_type: CreditRefType | null;
  ref_id: string | null;
  created_by: string | null;
  metadata?: Record<string, any> | null;
}

export interface GenerationsFilters {
  userId?: string;
  userEmail?: string;
  status?: GenerationStatus;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface UserWithStatusExtended extends UserWithStatus {
  credit_balance: number;
  generation_count: number;
}

// =========================================
// SALES METRICS TYPES
// =========================================

export interface SalesMetrics {
  gross_revenue_usd: number;
  /** Stripe charges in the currency paid. Absent on older responses. */
  revenue_gbp?: number;
  revenue_usd?: number;
  revenue_eur?: number;
  sales_with_amount?: number;
  sales_without_amount?: number;
  /** Paid credits plus a matching bonus, such as an ImageMotion double-credit offer. */
  credits_granted?: number;
  credits_sold: number;
  credits_used: number;
  credits_refunded: number;
  total_cost_usd: number;
  gross_margin_usd: number;
  gross_margin_pct: number;
  completed_generations: number;
  failed_generations: number;
  total_generations: number;
  failed_rate_pct: number;
  avg_cost_per_generation: number;
  avg_margin_per_generation: number;
  credit_price_usd: number;
}

/** Admin: purchase row that makes up Sales & Costs revenue */
export interface SalesTransaction {
  id: string;
  user_id: string;
  email: string | null;
  amount: number;
  /** Paid credits plus a matching bonus. Falls back to amount. */
  credits_granted?: number;
  type: string;
  description: string;
  created_at: string;
  stripe_session_id: string | null;
  currency: string | null;
  app: string | null;
  /** Stripe charge in minor units. Null when no charge is on file. */
  paid_amount_cents?: number | null;
  paid_currency?: string | null;
}

export type SalesPeriod = 'today' | '7d' | '30d' | 'all';

// =========================================
// SHARE DEMO TYPES
// =========================================

export type ShareDemoStatus = 'draft' | 'published' | 'unpublished';
export type ShareDemoDownloadMode = 'disabled' | 'free' | 'email_gate';
export type ShareDemoCtaMode = 'none' | 'signup' | 'demo' | 'both';
export type ShareDemoEventType = 'view' | 'slider_interaction' | 'download_click' | 'download_complete' | 'email_capture' | 'cta_click';
export type ShareDemoWorkflowType = 'photoclear' | 'hyperdusk' | 'video' | 'tour' | 'staging';
export type ShareDemoSceneType = 'exterior' | 'interior';

export interface ShareDemo {
  id: string;
  slug: string;
  internal_name: string;
  public_headline: string;
  public_subheadline: string;
  agency_name: string | null;
  agency_logo_url: string | null;
  source_label: string | null;
  original_image_url: string;
  processed_image_url: string | null;
  processed_video_url: string | null;
  processed_video_path: string | null;
  workflow_type: ShareDemoWorkflowType;
  tour_url: string | null;
  hyperdusk_project_id: string | null;
  hyperdusk_image_id: string | null;
  hyperdusk_preset_id: string | null;
  hyperdusk_scene_type: ShareDemoSceneType | null;
  hyperdusk_animation_style: string | null;
  hyperdusk_duration_seconds: number | null;
  apps_used: string[] | null;
  listing_url: string | null;
  status: ShareDemoStatus;
  download_mode: ShareDemoDownloadMode;
  watermark_enabled: boolean;
  branding_enabled: boolean;
  cta_mode: ShareDemoCtaMode;
  cta_signup_url: string;
  cta_demo_url: string;
  admin_notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

export interface ShareDemoEvent {
  id: string;
  share_demo_id: string;
  event_type: ShareDemoEventType;
  visitor_id: string | null;
  session_id: string | null;
  email: string | null;
  first_name: string | null;
  agency_name: string | null;
  user_agent: string | null;
  referrer: string | null;
  ip_address: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

export interface ShareDemoPublic {
  id: string;
  slug: string;
  public_headline: string;
  public_subheadline: string;
  agency_name: string | null;
  agency_logo_url: string | null;
  source_label: string | null;
  original_image_url: string;
  processed_image_url: string;
  processed_video_url: string | null;
  workflow_type: ShareDemoWorkflowType;
  tour_url: string | null;
  hyperdusk_preset_id: string | null;
  hyperdusk_scene_type: ShareDemoSceneType | null;
  hyperdusk_animation_style: string | null;
  hyperdusk_duration_seconds: number | null;
  apps_used: string[] | null;
  listing_url: string | null;
  download_mode: ShareDemoDownloadMode;
  watermark_enabled: boolean;
  branding_enabled: boolean;
  cta_mode: ShareDemoCtaMode;
  cta_signup_url: string;
  cta_demo_url: string;
}

export interface ShareDemoAnalyticsSummary {
  views: number;
  unique_visitors: number;
  slider_interactions: number;
  download_clicks: number;
  download_completes: number;
  emails_captured: number;
  cta_clicks: number;
  recent_leads: Array<{
    email: string;
    first_name: string | null;
    agency_name: string | null;
    created_at: string;
  }>;
}

// ---- Admin: Signups dashboard (admin_get_signups RPC) ----
export interface SignupRow {
  id: string;
  email: string;
  name: string;
  created_at: string;
  signup_app: string;
  provider: string;
  email_domain?: string;
  company?: string | null;
  credits?: number;
  last_sign_in_at?: string | null;
  email_confirmed?: boolean;
  marketing_consent?: boolean;
  referred_by_code?: string | null;
  subscription_plan?: string | null;
  has_purchased?: boolean;
  credits_purchased?: number;
  ip_address?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  landing_page?: string | null;
  attribution_captured_at?: string | null;
  acquisition_source?: string | null;
}

export interface SignupDayPoint {
  day: string;
  google: number;
  email: number;
  other: number;
  total: number;
}

export interface SignupMetrics {
  success: boolean;
  days: number;
  from?: string;
  to?: string;
  granularity?: 'hour' | 'day' | 'week';
  truncated?: boolean;
  stats: {
    total: number;
    google_ads?: number;
    today: number;
    last_24h: number;
    last_7d: number;
    last_30d: number;
    all_time: number;
  };
  by_provider: Record<string, number>;
  by_app: Record<string, number>;
  per_day: SignupDayPoint[];
  signups: SignupRow[];
  error?: string;
}

export interface SignupStatsOptions {
  from?: string | null;
  to?: string | null;
  app?: string | null;
  provider?: string | null;
}
